// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { CustomAppState } from "@tps/App/Store/Reducers";
import * as React from "react";
import { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    state: state.components.contactUsForm.fieldValues["state"],
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StateStyles {
    stateSelect?: SelectPresentationProps;
}

export const stateStyles: StateStyles = {};

export const styles = stateStyles;

const State: React.FC<Props> = ({ fields, state, setFieldValue, loadCurrentCountries, countries }) => {
    const [stateErrorMessage, setStateErrorMessage] = React.useState("");
    const { isRequired, label, hintText } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const states = React.useMemo(() => {
        const selectedCountry = !countries ? null : countries.length > 1
            ? countries.find(country => country.abbreviation === "US") || countries[0]
            : countries[0];

        return selectedCountry?.states || [];
    }, [countries]);

    React.useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    React.useEffect(() => {
        contactFormContext.validators.state = validateState;
        if (typeof state !== "undefined") {
            validateState();
        }
    }, [state]);

    React.useEffect(() => {
        contactFormContext.validators.state = validateState;
    }, [isRequired]);

    const validateState = () => {
        const errorMessage = isRequired && !state ? (siteMessage("Field_Required", label) as string) : "";
        setStateErrorMessage(errorMessage);
        return !errorMessage;
    };

    const stateChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "state", value: event.target.value });
    };

    return (
        <Select
            {...styles.stateSelect}
            label={label}
            required={isRequired}
            value={state || ""}
            error={stateErrorMessage}
            onChange={stateChangeHandler}
        >
            <option value="">{hintText}</option>
            {states?.map((state: StateModel) => (
                <option key={state.name} value={state.name}>
                    {state.name}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(State),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "State",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select State",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
